import React, { useState } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { MapPin, Mail, Phone, MessageCircle } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RATE_LIMIT_MS = 60_000;
const RATE_LIMIT_KEY = 'contact_last_submit';
const CONTACT_API =
  process.env.REACT_APP_CONTACT_API ||
  'http://localhost/henrique_tempus/site_seta/setanovosite/backend/contato.php';

const isSpam = (form, startedAt) => {
  if (form.website?.trim()) return true;
  if (Date.now() - startedAt < 3000) return true;
  const links = (form.mensagem.match(/https?:\/\/|www\./gi) || []).length;
  if (links > 2) return true;
  // Nome/empresa com URL ou só números
  const suspicious = [form.nome, form.sobrenome, form.empresa].join(' ');
  if (/https?:\/\/|www\./i.test(suspicious)) return true;
  if (/^\d+$/.test(form.nome.trim())) return true;
  // Mensagem com muitos caracteres repetidos (aaaaa, !!!!!)
  if (/(.)\1{8,}/.test(form.mensagem)) return true;
  return false;
};

const isRateLimited = () => {
  try {
    const last = Number(sessionStorage.getItem(RATE_LIMIT_KEY) || 0);
    return Date.now() - last < RATE_LIMIT_MS;
  } catch {
    return false;
  }
};

const markSubmitted = () => {
  try {
    sessionStorage.setItem(RATE_LIMIT_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
};

const Contact = ({ intro }) => {
  const { toast } = useToast();
  const [startedAt] = useState(() => Date.now());
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    sobrenome: '',
    email: '',
    telefone: '',
    empresa: '',
    mensagem: '',
    website: '', // honeypot — deve permanecer vazio
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const emptyForm = { nome: '', sobrenome: '', email: '', telefone: '', empresa: '', mensagem: '', website: '' };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.mensagem) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha nome, email e mensagem.',
      });
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast({
        title: 'Email inválido',
        description: 'Informe um endereço de email válido.',
      });
      return;
    }
    if (form.mensagem.trim().length < 10) {
      toast({
        title: 'Mensagem muito curta',
        description: 'Escreva pelo menos 10 caracteres na mensagem.',
      });
      return;
    }
    if (isRateLimited()) {
      toast({
        title: 'Aguarde um momento',
        description: 'Você já enviou uma mensagem recentemente. Tente novamente em breve.',
      });
      return;
    }
    // Spam: responde como sucesso para não dar feedback ao bot
    if (isSpam(form, startedAt)) {
      toast({
        title: 'Obrigado(a)!',
        description: 'Sua mensagem foi recebida. Entraremos em contato em breve.',
      });
      setForm(emptyForm);
      return;
    }

    setSending(true);
    try {
      const res = await fetch(CONTACT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: form.nome.trim(),
          sobrenome: form.sobrenome.trim(),
          email: form.email.trim(),
          telefone: form.telefone.trim(),
          empresa: form.empresa.trim(),
          mensagem: form.mensagem.trim(),
          website: form.website,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Falha ao enviar.');
      }
      markSubmitted();
      toast({
        title: 'Obrigado(a)!',
        description: 'Sua mensagem foi recebida. Entraremos em contato em breve.',
      });
      setForm(emptyForm);
    } catch (err) {
      toast({
        title: 'Erro ao enviar',
        description: err.message || 'Não foi possível enviar sua mensagem. Tente novamente.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contato" className="relative py-24 px-6" style={{ background: 'linear-gradient(180deg, #2a2a2a 0%, #3a3a3a 100%)' }}>
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#E8C16A] tracking-[0.4em] text-[12px] font-semibold mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            CONTATO
          </p>
          <h2 className="text-white text-[36px] md:text-[44px] font-bold tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Contato
          </h2>
          <div className="mx-auto mt-4 w-24 h-[3px] bg-[#C99A36]" />
          {intro && (
            <p
              className="mt-8 text-white/85 text-[15px] md:text-[16px] max-w-2xl mx-auto"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
            >
              {intro}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {/* Honeypot anti-spam — oculto para humanos */}
          <div className="absolute -left-[9999px] opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={update('website')}
            />
          </div>
          <Field label="Nome *">
            <Input value={form.nome} onChange={update('nome')} className="bg-[#C99A36]/85 border-0 rounded-none text-white placeholder:text-white/70 focus-visible:ring-2 focus-visible:ring-[#E8C16A] h-11" />
          </Field>
          <Field label="Sobrenome">
            <Input value={form.sobrenome} onChange={update('sobrenome')} className="bg-[#C99A36]/85 border-0 rounded-none text-white placeholder:text-white/70 focus-visible:ring-2 focus-visible:ring-[#E8C16A] h-11" />
          </Field>
          <Field label="Email *">
            <Input type="email" value={form.email} onChange={update('email')} className="bg-[#C99A36]/85 border-0 rounded-none text-white placeholder:text-white/70 focus-visible:ring-2 focus-visible:ring-[#E8C16A] h-11" />
          </Field>
          <Field label="Telefone">
            <Input value={form.telefone} onChange={update('telefone')} className="bg-[#C99A36]/85 border-0 rounded-none text-white placeholder:text-white/70 focus-visible:ring-2 focus-visible:ring-[#E8C16A] h-11" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Empresa">
              <Input value={form.empresa} onChange={update('empresa')} className="bg-[#C99A36]/85 border-0 rounded-none text-white placeholder:text-white/70 focus-visible:ring-2 focus-visible:ring-[#E8C16A] h-11" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Insira uma mensagem *">
              <Textarea
                value={form.mensagem}
                onChange={update('mensagem')}
                rows={5}
                className="bg-[#C99A36]/85 border-0 rounded-none text-white placeholder:text-white/70 focus-visible:ring-2 focus-visible:ring-[#E8C16A] resize-none"
              />
            </Field>
          </div>
          <div className="md:col-span-2 flex justify-center mt-2">
            <Button
              type="submit"
              disabled={sending}
              className="rounded-full px-12 h-12 bg-[#E8C16A] hover:bg-[#C99A36] text-[#1a1a1a] font-bold tracking-[0.15em] transition-colors disabled:opacity-60"
            >
              {sending ? 'Enviando…' : 'Enviar'}
            </Button>
          </div>
        </form>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          <InfoItem icon={<MapPin size={22} />} title="Endereço" text="Rua Gastão Pereira Gonçalves, 217 - Primeiro Piso, Centro,  Paracatu-MG" />
          <InfoItem icon={<Mail size={22} />} title="Email" text="seta@setaconsultoria.com" />
          <InfoItem icon={<Phone size={22} />} title="Telefone" text="+55 (38) 3672-6200" />
          <InfoItem icon={<MessageCircle size={22} />} title="WhatsApp" text="+55 (38) 98822-0080" />
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="flex flex-col">
    <span className="text-white/90 text-[12px] tracking-[0.2em] font-semibold mb-2 uppercase">{label}</span>
    {children}
  </label>
);

const InfoItem = ({ icon, title, text }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="w-12 h-12 rounded-full border border-[#C99A36] flex items-center justify-center text-[#E8C16A]">
      {icon}
    </div>
    <div className="text-white/90 text-[11px] tracking-[0.25em] uppercase font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      {title}
    </div>
    <div className="text-white/75 text-[13px]" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
      {text}
    </div>
  </div>
);

export default Contact;
